import { MicOff } from 'lucide-react';
import { useIsSpeaking, useIsMuted } from './voiceStore.js';

/**
 * Discord voice indicators for a player bubble. Each reads ONE boolean out of the
 * voice store for its own Discord user id and renders nothing otherwise — so a
 * speech/mute toggle re-renders only this leaf, never the surrounding grid. Both
 * render nothing off an Activity (store empty / voice unavailable), so they're
 * safe to drop into shared bubble components used on the web surfaces too.
 */

/**
 * A soft green pulsing ring overlaid on a round avatar while its Discord user is
 * speaking. Absolutely positioned to fill a `position: relative` parent. The
 * pulse (and its suppression under prefers-reduced-motion) lives in tokens.css.
 */
export function SpeakingRing({
  discordUserId,
  radius = '50%',
}: {
  discordUserId?: string | null;
  radius?: string | number;
}) {
  const speaking = useIsSpeaking(discordUserId);
  if (!speaking) return null;
  return (
    <span
      className="voice-speaking-ring"
      aria-hidden
      style={{
        // inset 0 (NOT negative): the ring's box stays exactly the avatar box so
        // it never extends a scroll container's overflow (the roster list is a
        // scroll box — a larger ring would add a horizontal scrollbar + clip). The
        // green still reads outside the disc via the soft box-shadow, which does
        // not contribute to scroll overflow.
        position: 'absolute',
        inset: 0,
        borderRadius: radius,
        border: '2px solid var(--glow-speaking)',
        boxShadow:
          '0 0 8px 1px var(--glow-speaking-halo), inset 0 0 6px var(--glow-speaking-halo)',
        pointerEvents: 'none',
        zIndex: 4,
      }}
    />
  );
}

/**
 * A small grey crossed-out mic shown next to a name when the Discord user's mic
 * is muted (self- or server-mute). Renders nothing when unmuted.
 */
export function MutedMic({
  discordUserId,
  size = 14,
}: {
  discordUserId?: string | null;
  size?: number;
}) {
  const muted = useIsMuted(discordUserId);
  if (!muted) return null;
  return (
    <MicOff
      size={size}
      strokeWidth={2}
      color="var(--state-dead)"
      aria-label="muted"
      style={{ flexShrink: 0, opacity: 0.9 }}
    />
  );
}
